import React, { useState, useEffect } from 'react'
import { Heading, Text, Box, Stack, FormControl, Button, Input, Textarea, Select, Alert, FileChooser, SimpleBreadcrumbs } from '@/components/lib'
import { useParams, useNavigate, Link } from 'react-router'
import utils from '@/utils/utils'
import api from '../../../api'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

import countryCodes from '../../../utils/country_codes.json'

export const IRB_TYPES = [
  { key: 'none', name: 'None required' },
  { key: 'local', name: 'Local IRB/EC' },
  { key: 'central', name: 'Central IRB/EC' }
]
export const APPROVAL_STATUSES = [
  { key: 'pending', name: 'Pending', color: 'orange' },
  { key: 'onHold', name: 'On-hold', color: 'purple' },
  { key: 'selected', name: 'Selected', color: 'green' },
  { key: 'rejected', name: 'Rejected', color: 'red' }
]
export const ACTIVITY_STATUSES = [
  { key: 'pendingSelection', name: 'Pending selection', color: 'orange' },
  { key: 'selected', name: 'Selected', color: 'green' },
  { key: 'activeRecruiting', name: 'Active (recruiting)', color: 'green' },
  { key: 'activeNotRecruiting', name: 'Active (not recruiting)', color: 'green' },
  { key: 'closedToRecruitment', name: 'Closed to recruitment', color: 'purple' },
  { key: 'closed', name: 'Closed', color: 'red' }
]

export default function NewSite () {
  const [site, setSite] = useState()
  const [error, setError] = useState()
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [image, setImage] = useState()
  const [imageUrl, setImageUrl] = useState()
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressPostCode, setAddressPostCode] = useState('')
  const [addressCountry, setAddressCountry] = useState('')
  const [irb, setIrb] = useState('none')
  const [approvalStatus, setApprovalStatus] = useState('pending')
  const [activityStatus, setActivityStatus] = useState('pendingSelection')
  const { siteId } = useParams()
  const { trial, updateTrial, addSite } = useStore()
  useTitle('Edit Site', trial)
  const navigate = useNavigate()
  const isEditing = !!site?._id

  useEffect(() => {
    if (!siteId) return
    api.sites.get(siteId, d => {
      setSite(d)
      if (d.id) setId(d.id)
      if (d.name) setName(d.name)
      if (d.description) setDescription(d.description)
      if (d.image) setImage(d.image)
      if (d.imageUrl) setImageUrl(d.imageUrl)
      if (d.contactName) setContactName(d.contactName)
      if (d.contactEmail) setContactEmail(d.contactEmail)
      if (d.contactPhone) setContactPhone(d.contactPhone)
      if (d.addressLine1) setAddressLine1(d.addressLine1)
      if (d.addressLine2) setAddressLine2(d.addressLine2)
      if (d.addressCity) setAddressCity(d.addressCity)
      if (d.addressPostCode) setAddressPostCode(d.addressPostCode)
      if (d.addressCountry) setAddressCountry(d.addressCountry)
      if (d.irb) setIrb(d.irb)
      if (d.approvalStatus) setApprovalStatus(d.approvalStatus)
      if (d.activityStatus) setActivityStatus(d.activityStatus)
    })
  }, [siteId])

  function create () {
    const data = { id, name, description, image, contactName, contactEmail: contactEmail || null, contactPhone, addressLine1, addressLine2, addressCity, addressPostCode, addressCountry, irb, approvalStatus, activityStatus }
    if (isEditing) {
      api.sites.update(siteId, data, updatedSite => {
        addSite(updatedSite)
        utils.toast('success', 'Site updated')
        navigate(`/trials/${trial._id}/sites`)
      }, err => setError(err.message))
    } else {
      api.sites.create(trial._id, data, newSite => {
        addSite(newSite)
        updateTrial(trial._id, { siteCount: (trial.siteCount ?? 0) + 1 })
        utils.toast('success', 'Site created')
        navigate(`/trials/${trial._id}/sites`)
      }, err => setError(err.message))
    }
  }

  function updateImage (data) {
    if (data) {
      setImage(data.storedName)
      setImageUrl(data.expectedUrl)
    } else {
      setImage(null)
      setImageUrl(null)
    }
  }

  return (
    <Stack gap={5}>
      <SimpleBreadcrumbs items={[
        { label: 'Sites', to: `/trials/${trial._id}/sites` },
        { label: isEditing ? `Edit site ${site?.name}` : 'Create a new site' }
      ]}
      />
      <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={3}>
        <Heading as='h2' size='lg'>{isEditing ? `Edit site ${site?.name}` : 'Create a new site'}</Heading>
        {isEditing && site?._id &&
          <Button as={Link} to={`/trials/${trial._id}/sites/${site._id}/messages`} colorPalette='primary' variant='outline'>View message board</Button>}
      </Box>

      <Stack gap={3} p={3} bg='bg.surface' rounded='md' borderColor='border.default' borderWidth='1px'>
        <Heading size='md'>About your site</Heading>
        <FormControl label='Site ID' helperText='A unique identifier for your site'>
          <Input placeholder='SITE-001' value={id} onChange={e => setId(e.target.value)} />
        </FormControl>

        <FormControl label='Name' helperText='Give your site a unique name'>
          <Input placeholder='My Site' value={name} onChange={e => setName(e.target.value)} />
        </FormControl>

        <FormControl label='Description' helperText='Optional. Briefly describe the site'>
          <Textarea placeholder='Write a short description...' value={description} onChange={e => setDescription(e.target.value)} />
        </FormControl>

        <FormControl label='Site logo or image' helperText='Optional. Upload an image or logo to represent the site'>
          <Stack>
            {imageUrl
              ? <img src={imageUrl} style={{ width: 200 }} />
              : <Alert variant='left-accent'>No image is currently set.</Alert>}
            <Stack direction='row'>
              <FileChooser
                accept='image/*'
                forType='trial' forObject={trial}
                onComplete={updateImage}
                trigger={<Button colorPalette='primary' variant='outline'>Choose an image</Button>}
              />
              {image &&
                <Button onClick={e => updateImage({ storedName: null })}>Remove image</Button>}
            </Stack>
          </Stack>
        </FormControl>
      </Stack>

      <Stack gap={3} p={3} bg='bg.surface' rounded='md' borderColor='border.default' borderWidth='1px'>
        <Box>
          <Heading size='md'>Contact information</Heading>
          <Text>This information will allow participants to know where to find the site and who to contact if they get stuck.</Text>
        </Box>

        <FormControl label='Key contact name' helperText='Recommended. The name of a person representing the site for this trial'>
          <Input placeholder='Jane Smith' value={contactName} onChange={e => setContactName(e.target.value)} />
        </FormControl>

        <FormControl label='Key contact email' helperText='Recommended. An email address for the site (or person representing the site for this trial)'>
          <Input placeholder='jane@company.com' value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
        </FormControl>

        <FormControl label='Key contact phone' helperText='Recommended. A phone number for the site (or person representing the site for this trial)'>
          <Input placeholder='+44 1234 567890' value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
        </FormControl>

        <FormControl label='Site address' helperText='Recommended. A physical address for the site'>
          <Stack>
            <Input placeholder='Address line 1' value={addressLine1} onChange={e => setAddressLine1(e.target.value)} />
            <Input placeholder='Address line 2' value={addressLine2} onChange={e => setAddressLine2(e.target.value)} />
            <Input placeholder='City' value={addressCity} onChange={e => setAddressCity(e.target.value)} />
            <Input placeholder='Post Code' value={addressPostCode} onChange={e => setAddressPostCode(e.target.value)} />
            <Select placeholder='Country' value={addressCountry} onChange={e => setAddressCountry(e.target.value)}>
              <option key='UK1' value='United Kingdom'>United Kingdom</option>
              <option key='US1' value='United States'>United States</option>
              <hr />
              {countryCodes.map(country =>
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              )}
            </Select>
          </Stack>
        </FormControl>
      </Stack>

      <Stack gap={3} p={3} bg='bg.surface' rounded='md' borderColor='border.default' borderWidth='1px'>
        <Box>
          <Heading size='md'>Regulatory & approval</Heading>
          <Text>Use status indicators to manage the site's lifecycle.</Text>
        </Box>

        <FormControl label='Relevant IRB/EC' helperText='Choose the relevant approach to Institutional Review Board or Ethical Approval'>
          <Select value={irb} onChange={e => setIrb(e.target.value)}>
            {IRB_TYPES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </Select>
        </FormControl>

        <FormControl label='Approval status' helperText='Manage the approval process of this site'>
          <Select value={approvalStatus} onChange={e => setApprovalStatus(e.target.value)}>
            {APPROVAL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </Select>
        </FormControl>

        <FormControl label='Activity status' helperText='Manage the trial stage of this site'>
          <Select value={activityStatus} onChange={e => setActivityStatus(e.target.value)}>
            {ACTIVITY_STATUSES.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </Select>
        </FormControl>
      </Stack>

      <Box mt={10} ml={10} mr={10} position='sticky' style={{ bottom: 10 }} boxShadow='lg' p={3} rounded='md' bg='bg.accent'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          {error
            ? (
              <Alert status='error' title='There was a problem saving your site'>
                {error}. Please fix these issues and try again.
              </Alert>)
            : <Box />}
          <Stack direction='row' gap={2}>
            {!isEditing && <Button size='lg' colorPalette='primary' variant='ghost' onClick={() => navigate(`/trials/${trial._id}/sites`)}>Cancel</Button>}
            <Button size='lg' colorPalette='primary' onClick={create}>{isEditing ? 'Save site' : 'Create site'}</Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  )
}
